import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

export interface BillsReportFilters {
  status: string; // 'all' | 'pending' | 'partially_paid' | 'paid' | 'overdue'
  provider: string; // 'all' | provider documentId
  dueDateFrom: string;
  dueDateTo: string;
}

export interface BillsReportSummary {
  totalBills: number;
  totalOriginalAmount: number;
  totalPaid: number;
  totalDeductions: number;
  totalRemaining: number;
  byStatus: Record<string, number>;
}

/**
 * Fetches the dedicated bills report endpoint (GET /api/bills/report).
 * Filters are applied server-side; the endpoint returns summary + full results.
 */
export function useBillsReport(filters: BillsReportFilters) {
  const [summary, setSummary] = useState<BillsReportSummary | null>(null);
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.provider && filters.provider !== 'all') params.set('provider', filters.provider);
      if (filters.dueDateFrom) params.set('dueDateFrom', filters.dueDateFrom);
      if (filters.dueDateTo) params.set('dueDateTo', filters.dueDateTo);
      const qs = params.toString();
      const res: any = await apiService.get(`/bills/report${qs ? `?${qs}` : ''}`);
      setSummary(res?.summary || null);
      setResults(Array.isArray(res?.results) ? res.results : []);
    } catch (e: any) {
      setError(e?.error?.message || e?.message || 'Failed to load bills report');
    } finally {
      setIsLoading(false);
    }
  }, [filters.status, filters.provider, filters.dueDateFrom, filters.dueDateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return { summary, results, isLoading, error, refetch: fetchReport };
}
